import { Suspense, useContext } from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { useColorMode, Flex, Spinner } from '@chakra-ui/react';
import Styleguide from './components/Styleguide';
import LayoutStyleguide from './common/LayoutStyleguide';
import LayoutAuth from './common/LayoutAuth';
import LayoutPlain from './common/LayoutPlain';
import LayoutLogged from './common/LayoutLogged';
import LoginPage from './pages/public/LoginPage';
import ForgotPasswordPage from './pages/public/ForgotPasswordPage';
import ResetPasswordPage from './pages/public/ResetPasswordPage';
import ThankYouPage from './pages/public/ThankYouPage';
import VerifyingYourEmail from './pages/public/VerifyingYourEmailPage';
import DashboardPage from './pages/public/DashboardPage';
import AccountSettingsPage from './pages/client/AccountSettingsPage';
import HeaderNotLogged from './common/HeaderNotLogged';
import HeaderPlain from './common/HeaderPlain';
import { BillingProvider } from './store/client/BillingContext';
import {
  HEADER_TOP_XL,
  HEADER_TYPE_RESET,
  HEADER_TYPE_LOGIN,
  HEADER_TYPE_BACK,
} from './utils/consts';
import { AuthContext } from './context/AuthContext';
import RedirectPage from './pages/public/RedirectPage';
import VerifyingChangedEmailPage from './pages/public/VerifyingChangedEmailPage';
import UnlockAccountPage from './pages/public/UnlockAccountPage';
import { MeProvider } from './context/MeContext';
import UseMe from './hooks/useMe';
import EventsListPage from './pages/client/EventsListPage';
import EventsCreatePage from './pages/client/EventsCreatePage';
import EventsEditPage from './pages/client/EventsEditPage';
import ReadersListPage from './pages/client/ReadersListPage';
import PassesListPage from './components/client/passes-new/PassesListPage';
import ReadersCreatePage from './pages/client/ReadersCreatePage';
import ReadersEditPage from './pages/client/ReadersEditPage';
import PassesConfirmPage from './pages/client/PassesConfirmPage';
import PassesViewPage from './components/client/passes-new/PassesViewPage';
import ErrorNotFoundPage from './components/errors/ErrorNotFoundPage';
import EventsViewPage from './pages/client/EventsViewPage';
import ReadersViewPage from './pages/client/ReadersViewPage';
import LiveEventDataPage from './pages/client/LiveEventDataPage';
import DeveloperToolsPage from './pages/client/DeveloperToolsPage';
import MessageLogsPage from './pages/client/MessageLogsPage';
import MessagesPage from './pages/client/MessagesPage';
import BatchesPage from './pages/client/BatchesPage';
import BatchesCreatePage from './pages/client/BatchesCreatePage';
import BatchesUpdatePage from './pages/client/BatchesUpdatePage';
import ReaderConfigurationPage from './pages/client/ReaderConfigurationPage';
import FeatureFlagGuard from './common/FeatureFlagGuard';
import SubscriptionGate from './components/common/SubscriptionGate';
import SubscriptionPage from './pages/client/SubscriptionPage';
import PassDownloadTemplateEditPage from './pages/client/PassDownloadTemplateEditPage';
import PassDownloadTemplateCreatePage from './pages/client/PassDownloadTemplateCreatePage';
import PassDownloadTemplatesPage from './pages/client/PassDownloadTemplatesPage';
import SignUpPage from './pages/public/SignUpPage';
import BillingPage from './pages/client/BillingPage';
import UsagePage from './pages/client/UsagePage';
import PassTemplateBuilderPage from './components/client/pass-templates-new/PassTemplateBuilderPage';
import PassTemplatesPage from './components/client/pass-templates-new/PassTemplatesPage';
import MfaPage from './pages/public/MfaPage';
import MfaMethodSetUpPage from './pages/public/MfaMethodSetUpPage';
import MfaRecoveryCodePage from './pages/public/MfaRecoveryCodePage';
import { MfaProvider } from './context/MfaContext';
import MfaRouteGuard from './common/MfaRouteGuard';
import DesktopResolutionGate from './common/DesktopResolutionGate';
import PassesBuilderPage from './components/client/passes-new/PassesBuilderPage';

const LoadingFallback = () => (
  <Flex height="100vh" alignItems="center" justifyContent="center">
    <Spinner size="xl" thickness="3px" speed="0.6s" color="primaryDark" />
  </Flex>
);

function AppRoutes() {
  return (
    <BrowserRouter>
      <DesktopResolutionGate>
        <MfaProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* STYLEGUIDE */}
              <Route
                path="/styleguide"
                element={
                  <LayoutStyleguide>
                    <Styleguide />
                  </LayoutStyleguide>
                }
              />
              {/* Home route */}
              <Route element={<HomeRoute />}>
                <Route
                  path="/"
                  element={
                    <LayoutAuth
                      header={
                        <HeaderNotLogged
                          top={HEADER_TOP_XL}
                          type={HEADER_TYPE_LOGIN}
                        />
                      }
                      formPaddingTop={true}
                    >
                      <LoginPage />
                    </LayoutAuth>
                  }
                />
              </Route>
              {/* PUBLIC */}
              <Route element={<PublicRoutes />}>
                <Route path="/error" element={<ErrorNotFoundPage />} />
                <Route
                  path="/confirm/:confirmationToken"
                  element={
                    <LayoutAuth formPaddingTop={true}>
                      <VerifyingYourEmail />
                    </LayoutAuth>
                  }
                />
                <Route
                  path="/unlock/:token"
                  element={
                    <LayoutPlain header={<HeaderPlain top={HEADER_TOP_XL} />}>
                      <UnlockAccountPage />
                    </LayoutPlain>
                  }
                />
                <Route
                  path="/email/confirm/:confirmationToken"
                  element={
                    <LayoutPlain header={<HeaderPlain top={HEADER_TOP_XL} />}>
                      <VerifyingChangedEmailPage />
                    </LayoutPlain>
                  }
                />
                <Route element={<PrivateRoutesWithoutSubscriptionGate />}>
                  <Route
                    path="/subscription"
                    element={
                      <FeatureFlagGuard flagKey="billing_enabled">
                        <SubscriptionPage />
                      </FeatureFlagGuard>
                    }
                  />
                </Route>

                {/* AUTH  */}
                <Route element={<AuthRoute />}>
                  <Route
                    path="/login"
                    element={
                      <LayoutAuth
                        header={
                          <HeaderNotLogged
                            top={HEADER_TOP_XL}
                            type={HEADER_TYPE_LOGIN}
                          />
                        }
                        formPaddingTop={true}
                      >
                        <LoginPage />
                      </LayoutAuth>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <LayoutAuth
                        header={
                          <HeaderNotLogged
                            top={HEADER_TOP_XL}
                            type={HEADER_TYPE_RESET}
                          />
                        }
                        backLink={true}
                        formPaddingTop={true}
                      >
                        <ForgotPasswordPage />
                      </LayoutAuth>
                    }
                  />
                  <Route
                    path="/password-reset/:resetToken"
                    element={
                      <LayoutAuth
                        header={
                          <HeaderNotLogged
                            top={HEADER_TOP_XL}
                            type={HEADER_TYPE_RESET}
                          />
                        }
                        formPaddingTop={true}
                      >
                        <ResetPasswordPage />
                      </LayoutAuth>
                    }
                  />
                  <Route path="/signup" element={<SignUpPage />} />
                </Route>
                <Route
                  path="/thank-you"
                  element={
                    <LayoutAuth
                      header={
                        <HeaderNotLogged
                          top={HEADER_TOP_XL}
                          type={HEADER_TYPE_BACK}
                        />
                      }
                      formPaddingTop={true}
                    >
                      <ThankYouPage />
                    </LayoutAuth>
                  }
                />
              </Route>

              {/* PRIVATE */}
              <Route element={<PrivateRoutes />}>
                <Route
                  path="/mfa"
                  element={
                    <LayoutAuth>
                      <FeatureFlagGuard flagKey="mfa_enabled">
                        <MfaPage />
                      </FeatureFlagGuard>
                    </LayoutAuth>
                  }
                />
                <Route
                  path="/mfa/setup/:type"
                  element={
                    <LayoutAuth>
                      <FeatureFlagGuard flagKey="mfa_enabled">
                        <MfaMethodSetUpPage />
                      </FeatureFlagGuard>
                    </LayoutAuth>
                  }
                />
                <Route
                  path="/mfa/setup/recovery-code"
                  element={
                    <LayoutAuth>
                      <FeatureFlagGuard flagKey="mfa_enabled">
                        <MfaRecoveryCodePage />
                      </FeatureFlagGuard>
                    </LayoutAuth>
                  }
                />
                {/* <Route
              path="/pass-templates-new"
              element={
                <LayoutLogged>
                  <PassTemplateBuilderPage />
                </LayoutLogged>
              }
            /> */}
                <Route
                  path="/passes"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard
                        flagKey="pass_builder_enabled"
                        path="/passes"
                      >
                        <PassesListPage />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/passes/create"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard
                        flagKey="pass_builder_enabled"
                        path="/passes/create"
                      >
                        <PassesBuilderPage isCreate />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/passes/batches"
                  element={
                    <LayoutLogged>
                      <BatchesPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/passes/batches/create"
                  element={
                    <LayoutLogged>
                      <BatchesCreatePage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/passes/batches/update"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard flagKey="batch_pass_updates_enabled">
                        <BatchesUpdatePage />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/passes/confirm/:uuid"
                  element={
                    <LayoutLogged>
                      <PassesConfirmPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/passes/:uuid"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard
                        flagKey="pass_builder_enabled"
                        path="/passes/:uuid"
                      >
                        <PassesViewPage />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/passes/edit/:uuid"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard
                        flagKey="pass_builder_enabled"
                        path="/passes/edit/:uuid"
                      >
                        <PassesBuilderPage isEdit />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/live-data"
                  element={
                    <LayoutLogged>
                      <LiveEventDataPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/pass-templates"
                  element={
                    <LayoutLogged>
                      <PassTemplatesPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/pass-templates/create"
                  element={
                    <LayoutLogged>
                      <PassTemplateBuilderPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/pass-templates/edit/:uuid"
                  element={
                    <LayoutLogged>
                      <PassTemplateBuilderPage isEdit />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/pass-download-pages"
                  element={
                    <LayoutLogged>
                      <PassDownloadTemplatesPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/pass-download-pages/create"
                  element={
                    <LayoutLogged>
                      <PassDownloadTemplateCreatePage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/pass-download-pages/edit/:uuid"
                  element={
                    <LayoutLogged>
                      <PassDownloadTemplateEditPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/events"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard flagKey="events_enabled">
                        <EventsListPage />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/events/create"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard flagKey="events_enabled">
                        <EventsCreatePage />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/events/edit/:uuid"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard flagKey="events_enabled">
                        <EventsEditPage />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/events/:uuid"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard flagKey="events_enabled">
                        <EventsViewPage />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/account-settings"
                  element={
                    <LayoutLogged>
                      <AccountSettingsPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/my-account"
                  element={
                    <LayoutLogged>
                      <BillingPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/usage"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard flagKey="billing_enabled">
                        <UsagePage />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/developer-tools"
                  element={
                    <LayoutLogged>
                      <DeveloperToolsPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/readers"
                  element={
                    <LayoutLogged>
                      <ReadersListPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/readers/create"
                  element={
                    <LayoutLogged>
                      <ReadersCreatePage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/readers/:uuid"
                  element={
                    <LayoutLogged>
                      <ReadersViewPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/readers/edit/:uuid"
                  element={
                    <LayoutLogged>
                      <ReadersEditPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/readers/reader-configuration"
                  element={
                    <LayoutLogged>
                      <ReaderConfigurationPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/messaging"
                  element={
                    <LayoutLogged>
                      <MessageLogsPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/messaging/create"
                  element={
                    <LayoutLogged>
                      <MessagesPage />
                    </LayoutLogged>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <LayoutLogged>
                      <FeatureFlagGuard flagKey="billing_enabled">
                        <BillingPage />
                      </FeatureFlagGuard>
                    </LayoutLogged>
                  }
                />
                {/* <Route path="/logout" element={<LogoutPage />} /> */}
                <Route
                  path="*"
                  element={
                    <LayoutLogged>
                      <RedirectPage />
                    </LayoutLogged>
                  }
                />
              </Route>
              <Route element={<PrivateRoutes />}>
                <Route
                  path="/dashboard"
                  element={
                    <LayoutLogged>
                      <DashboardPage />
                    </LayoutLogged>
                  }
                />
              </Route>
            </Routes>
          </Suspense>
        </MfaProvider>
      </DesktopResolutionGate>
    </BrowserRouter>
  );
}

function PrivateRoutes() {
  const { isAuthenticated, authLoading, authState } = useContext(AuthContext);
  const location = useLocation();
  const hasCachedAccount =
    authState.account && Object.keys(authState.account).length > 0;

  if (authLoading) {
    if (!hasCachedAccount) return <LoadingFallback />;
  } else if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ next: location }} replace />;
  }

  const isMfaRoute = location.pathname.startsWith('/mfa');

  if (isMfaRoute) {
    return <Outlet />;
  }

  return (
    <MfaRouteGuard>
      <MeProvider>
        <UseMe>
          <SubscriptionGate>
            <BillingProvider>
              <Outlet />
            </BillingProvider>
          </SubscriptionGate>
        </UseMe>
      </MeProvider>
    </MfaRouteGuard>
  );
}

function PrivateRoutesWithoutSubscriptionGate() {
  const { isAuthenticated, authLoading, authState } = useContext(AuthContext);
  const location = useLocation();
  const hasCachedAccount =
    authState.account && Object.keys(authState.account).length > 0;

  if (authLoading && !hasCachedAccount) {
    return <LoadingFallback />;
  }

  if (!authLoading && !isAuthenticated()) {
    return <Navigate to="/login" state={{ next: location }} replace />;
  }

  return isAuthenticated() || hasCachedAccount ? (
    <MfaRouteGuard>
      <MeProvider>
        <UseMe>
          <Outlet />
        </UseMe>
      </MeProvider>
    </MfaRouteGuard>
  ) : (
    <Navigate to="/login" state={{ next: location }} replace />
  );
}

// MODE ALWAYS LIGHT ROUTES
function PublicRoutes() {
  const { colorMode, toggleColorMode } = useColorMode();
  if (colorMode === 'dark') {
    toggleColorMode();
  }
  return <Outlet />;
}

function AuthRoute() {
  const { isAuthenticated, authLoading, authState } = useContext(AuthContext);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const { colorMode, toggleColorMode } = useColorMode();
  if (colorMode === 'dark') {
    toggleColorMode();
  }

  if (location.pathname === '/login' && urlParams.get('impersonationUuid')) {
    return <Outlet />;
  }

  const hasCachedAccount =
    authState.account && Object.keys(authState.account).length > 0;

  if (authLoading) {
    if (hasCachedAccount) return <Navigate to={'/dashboard'} replace />;
    return <LoadingFallback />;
  }

  return isAuthenticated() ? (
    <Navigate to={'/dashboard'} replace />
  ) : (
    <Outlet />
  );
}

function HomeRoute() {
  const { isAuthenticated, authLoading, authState } = useContext(AuthContext);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  if (location.pathname === '/login' && urlParams.get('impersonationUuid')) {
    return <Outlet />;
  }

  const hasCachedAccount =
    authState.account && Object.keys(authState.account).length > 0;

  if (authLoading) {
    if (hasCachedAccount) return <Navigate to="/dashboard" />;
    return <LoadingFallback />;
  }

  return isAuthenticated() ? <Navigate to="/dashboard" /> : <Outlet />;
}

export default AppRoutes;
